import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "@phosphor-icons/react";
import OperatorPageTip from "@/components/OperatorPageTip";

/**
 * Iter209br — Guida "Come funzionano i tuoi compensi" per operatori OUT.
 *
 * - `mode="tooltip"` (default): renderizza `OperatorPageTip` che compare
 *   una sola volta alla prima apertura della pagina Compensi.
 * - `mode="button"`: renderizza un pulsante "Guida compensi" sempre
 *   accessibile che apre il dialog anche dopo il primo onboarding.
 *
 * L'guida completa (dialog) è la stessa in entrambi i casi.
 */
export const COMPENSI_GUIDE_STEPS = [
  {
    title: "Compensi per servizio",
    body: "Ogni servizio marketplace ha un compenso predefinito (fasce interne). Quando accetti un incarico, il compenso viene calcolato secondo il listino Housetag per il tuo profilo OUT.",
  },
  {
    title: "Quando ricevi il pagamento",
    body: "Il compenso maturato viene liquidato dopo che il cliente conferma la chiusura pratica e completa il pagamento a Housetag. Vedi ogni movimento nella tab \"Compensi\" del tuo profilo.",
  },
  {
    title: "Trattenute e fatturazione",
    body: "Housetag opera come intermediario: emette fattura al cliente e ti gira il compenso al netto delle sole commissioni platform (se applicabili al tuo contratto). Le trattenute sono sempre visibili sul dettaglio.",
  },
  {
    title: "Storico e report",
    body: "Nella pagina Compensi trovi lo storico completo, filtri per periodo e servizio, e l'export CSV per la tua contabilità.",
  },
];

export default function CompensiGuide({ mode = "button" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const isOut = user?.role?.endsWith?.("_operator") && user?.operator_type === "out";
  if (!isOut) return null;

  if (mode === "tooltip") {
    return (
      <>
        <OperatorPageTip
          pageKey="compensi"
          title="Come funzionano i tuoi compensi"
          body="Sei un operatore OUT: sotto trovi il tuo storico compensi Housetag. Apri la guida per capire come sono calcolati, quando vengono liquidati e le trattenute applicate."
          outOnly
          actions={
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-xs text-purple-700 hover:underline"
              data-testid="compensi-guide-open-from-tip"
            >
              Apri la guida completa →
            </button>
          }
        />
        <CompensiGuideDialog open={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => setOpen(true)}
        data-testid="compensi-guide-open"
      >
        <Info size={12} weight="fill" className="mr-1" /> Guida compensi
      </Button>
      <CompensiGuideDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function CompensiGuideDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="compensi-guide-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Come funzionano i tuoi compensi</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          {COMPENSI_GUIDE_STEPS.map((s, i) => (
            <div key={i} className="border-l-2 border-purple-300 pl-3">
              <div className="font-semibold text-slate-900">{s.title}</div>
              <p className="text-slate-600 text-xs leading-relaxed mt-0.5">{s.body}</p>
            </div>
          ))}
          <div className="text-[11px] text-slate-500 pt-2 border-t">
            Per dubbi contrattuali o problemi di pagamento scrivi ad amministrazione all'indirizzo <a href="mailto:amministrazione@housetag.it" className="underline">amministrazione@housetag.it</a>.
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="compensi-guide-close">Chiudi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
